import { Dispatch, SetStateAction, useMemo } from "react"
import {
  Block,
  Button,
  Divider,
  StatefulButtonGroup,
  useStyletron,
} from "@mezo-org/mezo-clay"

import {
  selectMinimumCollateralRatioInCurrentMode,
  useDappSelector,
} from "#/redux-state"
import { ENABLE_BORROW_LIQUIDATIONS_PRICES } from "#/shared/constants"
import {
  useChangeInLiquidationPriceOnCollateralChange,
  useManageCollateralAmount,
  useManageCollateralRatio,
} from "#/shared/hooks"
import {
  BorrowCollateralManagementActionUnit,
  BorrowCollateralManagementStrategy,
} from "#/shared/types"
import { bigIntToFloat, getCurrencySymbol } from "#/shared/utils"
import { AmountInput } from "#/ui/Input"
import { ComponentStepProps } from "#/ui/Modal"

import { BorrowProgress } from "../components"

import ManageSetupItem from "./ManageSetupItem"

export type ManageSetupProps = ComponentStepProps & {
  activeSetupAction: BorrowCollateralManagementActionUnit
  setActiveSetupAction: Dispatch<
    SetStateAction<BorrowCollateralManagementActionUnit>
  >
}

const getInputPlaceholder = (
  isDepositing: boolean,
  isSettingAmount: boolean,
) => {
  if (isDepositing)
    return isSettingAmount ? `0 ${getCurrencySymbol("mBTC")}` : "0%"

  return isSettingAmount ? `-0 ${getCurrencySymbol("mBTC")}` : "-0%"
}

const getInputAmount = (
  isDepositing: boolean,
  isSettingAmount: boolean,
  amountCollateral: string,
  amountRatio: string,
) => {
  if (isSettingAmount && !amountCollateral) return ""

  if (!isSettingAmount && !amountRatio) return ""

  if (isDepositing) {
    return isSettingAmount ? amountCollateral : amountRatio
  }

  const trimMinusSign = (value: string) =>
    value.startsWith("-") ? value.slice(1) : value

  return `-${trimMinusSign(isSettingAmount ? amountCollateral : amountRatio)}`
}

export default function ManageSetup({
  onClickContinue,
  activeSetupAction,
  setActiveSetupAction,
}: ManageSetupProps) {
  const [css, theme] = useStyletron()

  const minimumCollateralRatio = useDappSelector(
    selectMinimumCollateralRatioInCurrentMode,
  )

  const minimumCollateralRatioLabel = bigIntToFloat(
    (minimumCollateralRatio ?? 0n) * 100n,
  )

  const {
    updateCollateralStrategy,
    updateCollateralAmount,
    updateCollateralRatio,
    onCollateralAmountChange,
    // We've hidden max amount here for now to make it easier for users to
    // pick the safe values that won't put the loan at risk
    // onMaxAmountClick,
    isValidCollateral,
    availableFundsBalanceLabel,
    prevCollateralAmount,
    prevCollateralAmountLabel,
    newCollateralAmount,
    newCollateralAmountLabel,
  } = useManageCollateralAmount(activeSetupAction)

  const {
    isValidRatio,
    prevCollateralizationRatioLabel,
    newCollateralizationRatioLabel,
    collateralizationRatioChangeLabel,
  } = useManageCollateralRatio()

  const {
    newLiquidationPriceLabel,
    changeInLiquidationPriceLabelWithUSD,
    prevLiquidationPriceLabel,
  } = useChangeInLiquidationPriceOnCollateralChange(
    prevCollateralAmount,
    newCollateralAmount,
  )

  const isDepositingCollateral =
    updateCollateralStrategy ===
    BorrowCollateralManagementStrategy.DEPOSIT_COLLATERAL

  const isSettingAmount =
    activeSetupAction === BorrowCollateralManagementActionUnit.AMOUNT

  const inputValue = getInputAmount(
    isDepositingCollateral,
    isSettingAmount,
    updateCollateralAmount,
    updateCollateralRatio,
  )

  const hasError = !isValidCollateral || !isValidRatio

  const errorMessage = useMemo(() => {
    if (!isValidRatio) return "Invalid collateralization ratio."

    if (!isValidCollateral) return "Insufficient funds."

    return ""
  }, [isValidCollateral, isValidRatio])

  return (
    <Block flex={1}>
      <BorrowProgress step={1} stepsCount={4} />
      <Block
        className={css({
          paddingInline: theme.sizing.scale800,
          display: "flex",
          justifyContent: "space-between",
          flexDirection: "column",
          height: "calc(100% - 40px)",
        })}
      >
        <Block
          className={css({
            display: "flex",
            flexDirection: "column",
            gap: theme.sizing.scale300,
          })}
        >
          <StatefulButtonGroup
            mode="radio"
            initialState={{ selected: isSettingAmount ? 0 : 1 }}
            shape="pill"
            size="small"
            overrides={{
              Root: {
                style: () => ({
                  background: theme.colors.gray100,
                  borderRadius: "999px",
                  padding: "4px",
                }),
              },
            }}
          >
            <Button
              overrides={{ BaseButton: { style: () => ({ flex: 1 }) } }}
              onClick={() =>
                setActiveSetupAction(
                  BorrowCollateralManagementActionUnit.AMOUNT,
                )
              }
            >
              Custom amount
            </Button>
            <Button
              overrides={{ BaseButton: { style: () => ({ flex: 1 }) } }}
              onClick={() =>
                setActiveSetupAction(BorrowCollateralManagementActionUnit.RATIO)
              }
            >
              Collateral ratio %
            </Button>
          </StatefulButtonGroup>

          <AmountInput
            label={
              isSettingAmount
                ? "Enter collateral amount"
                : "Enter the collateral ratio %"
            }
            token="mBTC"
            placeholder={getInputPlaceholder(
              isDepositingCollateral,
              isSettingAmount,
            )}
            unitLabel={isSettingAmount ? ` ${getCurrencySymbol("mBTC")}` : "%"}
            onChange={(ev) => onCollateralAmountChange(ev.target.value)}
            value={inputValue}
            balanceLabel={isDepositingCollateral ? "Balance" : "Max withdrawal"}
            balanceToken={availableFundsBalanceLabel}
            // onButtonMax={onMaxAmountClick}
            error={!!inputValue && hasError}
            hasBalanceError={!isValidCollateral}
            errorMessage={errorMessage}
            autoFocus
          />
        </Block>
        <Block>
          <Block
            className={css({
              display: "flex",
              flexDirection: "column",
              gap: theme.sizing.scale300,
              border: `1px solid ${theme.colors.borderOpaque}`,
              borderRadius: theme.borders.radius300,
              overflow: "hidden",
              marginBottom: theme.sizing.scale800,
              padding: `${theme.sizing.scale400} 0`,
            })}
          >
            <ManageSetupItem
              title="Collateralization ratio"
              value={`${newCollateralizationRatioLabel}%`}
              subTitle={
                updateCollateralAmount
                  ? `${
                      isDepositingCollateral ? "+" : ""
                    }${collateralizationRatioChangeLabel}%`
                  : undefined
              }
              subValue={
                updateCollateralAmount
                  ? `from ${prevCollateralizationRatioLabel}%`
                  : undefined
              }
              description={
                isDepositingCollateral
                  ? undefined
                  : `Keep a safety margin above the min. ${minimumCollateralRatioLabel}%`
              }
            />
            <Divider $size="cell" />
            <ManageSetupItem
              title="Collateral"
              value={`${newCollateralAmountLabel} ${getCurrencySymbol("mBTC")}`}
              subTitle={
                updateCollateralAmount
                  ? `${
                      isDepositingCollateral ? "+" : "-"
                    }${updateCollateralAmount} ${getCurrencySymbol("mBTC")}`
                  : undefined
              }
              subValue={
                updateCollateralAmount
                  ? `from ${prevCollateralAmountLabel} ${getCurrencySymbol(
                      "mBTC",
                    )}`
                  : undefined
              }
            />
            {ENABLE_BORROW_LIQUIDATIONS_PRICES && (
              <>
                <Divider $size="cell" />
                <ManageSetupItem
                  title="Liquidation price"
                  value={`$${newLiquidationPriceLabel}`}
                  subTitle={`${changeInLiquidationPriceLabelWithUSD}`}
                  subValue={`$${prevLiquidationPriceLabel}`}
                />
              </>
            )}
          </Block>
          <Button
            width="100%"
            data-attr={
              isDepositingCollateral
                ? "deposit_collateral-step_2"
                : "withdraw_collateral-step_2"
            }
            size="large"
            onClick={onClickContinue}
            disabled={hasError}
          >
            Review
          </Button>
        </Block>
      </Block>
    </Block>
  )
}
